import React, { useEffect, useMemo, useState } from 'react';
import { Box, Drawer, Tab, Tabs } from '@mui/material';
import { notify } from '../notifications';
import { apiErrorMessage } from '../admin/reports/edit/editApi';
import { setStaffStatus } from './staffApi';
import { impersonateStaffAndRedirect } from './staffImpersonation';
import { IssuedPassword, StaffDetailTab, StaffMember, StaffOperator, StaffPoint, StaffTarget, StaffTreeModule } from './types';
import { STAFF_PRIMARY } from './staffStyles';
import StaffDetailHeader from './StaffDetailHeader';
import StaffPermissionsPanel from './StaffPermissionsPanel';
import StaffPasswordPanel from './StaffPasswordPanel';
import StaffSessionsPanel from './StaffSessionsPanel';
import StaffJournalPanel from './StaffJournalPanel';

interface StaffDetailDrawerProps {
  target: StaffTarget;
  /** null - карточка закрыта */
  member: StaffMember | null;
  point: StaffPoint;
  tree: StaffTreeModule[];
  operator: StaffOperator;
  /** Лимит активных занят: разблокировать нельзя */
  limitFull: boolean;
  onIssued: (issued: IssuedPassword) => void;
  onChanged: () => void;
  onClose: () => void;
}

const TAB_TITLES: Record<StaffDetailTab, string> = {
  permissions: 'Права',
  password: 'Пароль',
  sessions: 'Сессии',
  journal: 'Журнал'
};

/** Вкладки по правам оператора: права видны всем, кто видит сотрудника, остальное - только при своём праве */
function tabsFor(operator: StaffOperator): StaffDetailTab[] {
  const tabs: StaffDetailTab[] = ['permissions'];
  if (operator.canResetPassword) tabs.push('password');
  if (operator.canSessions) tabs.push('sessions');
  if (operator.canAudit) tabs.push('journal');
  return tabs;
}

/** Карточка сотрудника точки - выезжает справа, как карточка в «Пользователях» */
const StaffDetailDrawer: React.FC<StaffDetailDrawerProps> = ({
  target,
  member,
  point,
  tree,
  operator,
  limitFull,
  onIssued,
  onChanged,
  onClose
}) => {
  const tabs = useMemo(() => tabsFor(operator), [operator]);
  const [tab, setTab] = useState<StaffDetailTab>('permissions');
  const [statusSaving, setStatusSaving] = useState(false);
  const [impersonating, setImpersonating] = useState(false);
  const memberId = member ? member.id : null;

  useEffect(() => { setTab('permissions'); }, [memberId]);

  const activeTab = tabs.indexOf(tab) >= 0 ? tab : 'permissions';

  const unblockBlockedReason = point.blocked
    ? 'Точка заблокирована: сначала разблокируйте точку'
    : limitFull
      ? 'Достигнут лимит активных сотрудников точки'
      : '';

  const handleToggleStatus = async () => {
    if (!member) return;
    setStatusSaving(true);
    try {
      notify.success(await setStaffStatus(target, member.id, !member.active));
      onChanged();
    } catch (err) {
      notify.error(apiErrorMessage(err, 'Не удалось изменить статус'));
    } finally {
      setStatusSaving(false);
    }
  };

  const handleImpersonate = async () => {
    if (!member) return;
    setImpersonating(true);
    try {
      await impersonateStaffAndRedirect(member.id);
    } catch (err) {
      notify.error(apiErrorMessage(err, 'Не удалось войти под сотрудником'));
      setImpersonating(false);
    }
  };

  return (
    <Drawer
      anchor="right"
      open={Boolean(member)}
      onClose={onClose}
      PaperProps={{ sx: { width: { xs: '100%', sm: '92%', md: '760px', lg: '880px' }, bgcolor: '#f8fafc' } }}
    >
      {member && (
        <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100%' }}>
          <StaffDetailHeader
            member={member}
            canBlock={operator.canBlock}
            unblockBlockedReason={unblockBlockedReason}
            canImpersonate={target.mode === 'admin' && operator.canImpersonate && member.active && !point.blocked}
            statusSaving={statusSaving}
            impersonating={impersonating}
            onToggleStatus={handleToggleStatus}
            onImpersonate={handleImpersonate}
            onClose={onClose}
          />

          {tabs.length > 1 && (
            <Box sx={{ position: 'sticky', top: 0, zIndex: 2, bgcolor: '#ffffff', borderBottom: '1px solid #e2e8f0', px: { xs: 1, sm: 2.5 } }}>
              <Tabs
                value={activeTab}
                onChange={(_, next: StaffDetailTab) => setTab(next)}
                variant="scrollable"
                scrollButtons={false}
                sx={{
                  minHeight: 46,
                  '& .MuiTab-root': { minHeight: 46, textTransform: 'none', fontWeight: 600, fontSize: '0.86rem', color: '#64748b' },
                  '& .Mui-selected': { color: `${STAFF_PRIMARY} !important` },
                  '& .MuiTabs-indicator': { bgcolor: STAFF_PRIMARY }
                }}
              >
                {tabs.map(id => <Tab key={id} value={id} label={TAB_TITLES[id]} />)}
              </Tabs>
            </Box>
          )}

          <Box sx={{ p: { xs: 2, sm: 3 }, flex: 1 }}>
            {activeTab === 'permissions' && (
              <StaffPermissionsPanel target={target} member={member} tree={tree} canEdit={operator.canEditPermissions} onSaved={onChanged} />
            )}
            {activeTab === 'password' && (
              <StaffPasswordPanel target={target} member={member} onIssued={onIssued} onReset={onChanged} />
            )}
            {activeTab === 'sessions' && <StaffSessionsPanel target={target} member={member} onChanged={onChanged} />}
            {activeTab === 'journal' && <StaffJournalPanel target={target} member={member} />}
          </Box>
        </Box>
      )}
    </Drawer>
  );
};

export default StaffDetailDrawer;
